import { Box, Typography, Skeleton } from '@mui/material';
import { colors } from '@src/styles';
import { GetTasksSummaryResponseDTO } from '@src/services/brain/modules/v1/tasks/dto/brain-tasks.getSummary.response.dto';
import {
  HourglassEmpty as PendingIcon,
  PlayArrow as InProgressIcon,
  CheckCircle as ConcludedIcon,
  Cancel as RejectedIcon,
} from '@mui/icons-material';

export const statusIcons = {
  PENDING: PendingIcon,
  IN_PROGRESS: InProgressIcon,
  CONCLUDED: ConcludedIcon,
  REJECTED: RejectedIcon,
};

export const statusColors: Record<string, string> = {
  PENDING: colors.status.pending,
  IN_PROGRESS: colors.status.running,
  CONCLUDED: colors.status.concluded,
  REJECTED: colors.status.rejected,
};

export const statusColorsText: Record<string, string> = {
  PENDING: colors.brand.ink,
  IN_PROGRESS: colors.brand.ink,
  CONCLUDED: colors.brand.ink,
  REJECTED: colors.brand.ink,
};

export const statusLabels: Record<string, string> = {
  PENDING: 'Aguardando',
  IN_PROGRESS: 'Em progresso',
  CONCLUDED: 'Concluído',
  REJECTED: 'Rejeitado',
};

const monoLabelSX = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '10px',
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
} as const;

export const StatusProgressBar = ({
  tasksSummary,
  isLoading = false,
}: {
  tasksSummary: GetTasksSummaryResponseDTO | null;
  isLoading?: boolean;
}) => {
  const statusCounts = {
    PENDING: tasksSummary?.pendingQuantity || 0,
    IN_PROGRESS: tasksSummary?.inProgressQuantity || 0,
    CONCLUDED: tasksSummary?.concludedQuantity || 0,
    REJECTED: tasksSummary?.rejectedQuantity || 0,
  };

  const totalTasks = tasksSummary?.total || 0;

  if (isLoading || !tasksSummary) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexDirection: 'column',
          height: 'auto',
        }}
      >
        {/* Progress bar skeleton */}
        <Skeleton
          variant="rectangular"
          width="100%"
          height={8}
          sx={{
            borderRadius: '999px',
            backgroundColor: colors.background.secondary,
          }}
        />
        {/* Legend skeleton */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton
                variant="rectangular"
                width={12}
                height={12}
                sx={{
                  borderRadius: '2px',
                  backgroundColor: colors.background.secondary,
                }}
              />
              <Skeleton
                variant="text"
                width={80}
                height={16}
                sx={{
                  backgroundColor: colors.background.secondary,
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexShrink: 0,
        flexDirection: 'column',
        height: 'auto',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          height: '8px',
          borderRadius: '999px',
          overflow: 'hidden',
          backgroundColor: colors.brand.surface,
        }}
      >
        {Object.entries(statusCounts).map(([status, count]) => {
          const percentage = (count / totalTasks) * 100;
          return (
            <Box
              key={status}
              sx={{
                width: `${percentage}%`,
                backgroundColor: statusColors[status] || colors.background.secondary,
                transition: 'width 0.3s',
              }}
            />
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
        {Object.entries(statusCounts).map(([status, count]) => (
          <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                backgroundColor: statusColors[status] || colors.background.secondary,
                borderRadius: '999px',
              }}
            />
            <Typography sx={{ ...monoLabelSX, color: colors.brand.textSecondary }}>
              {statusLabels[status] || status} · {count}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
